<template>
  <v-card class="order-modal-container">
    <v-card-title class="pb-0">{{ title }}</v-card-title>
    <v-card-text class="pb-0">
      <v-row justify="center">
        <v-col cols="12" md="auto" class="flex-fill">
          <template v-if="!initialized">
            <v-skeleton-loader type="list-item-avatar-two-line" tile />
            <v-skeleton-loader type="list-item-avatar-two-line" tile />
          </template>
          <template v-else>
            <div v-for="(order, index) of orders" :key="order.id">
              <h3>
                Pedido #{{ index + 1 }}
                <v-chip
                  dark
                  :color="getStatusColor(order.status)"
                  x-small
                  label
                  >{{ getStatusName(order.status) }}</v-chip
                >
                <v-chip
                  dark
                  :color="getPaymentStatusColor(order.paymentStatus)"
                  x-small
                  label
                  >{{ getPaymentStatusName(order.paymentStatus) }}</v-chip
                >
              </h3>
              <div class="children-inline">
                <timeago
                  class="time-ago grey--text font-weight-light"
                  :datetime="order.createdAt"
                  :auto-update="30"
                ></timeago>
                <span class="grey--text font-weight-light"
                  >por {{ order.user ? order.user.name : 'Anónimo' }}</span
                >
              </div>
              <div class="order-items">
                <v-row
                  align="center"
                  class="order-item flex-nowrap"
                  v-for="(orderItem, index) of order.orderItems"
                  :key="index"
                >
                  <v-col cols="auto">
                    <span class="amount">{{ orderItem.amount }}x</span>
                  </v-col>
                  <v-col cols="auto" class="px-0" v-if="orderItem.item.picture">
                    <v-img
                      class="picture"
                      :src="`${uploadsPath}/${orderItem.item.picture}`"
                    ></v-img>
                  </v-col>
                  <v-col class="meta flex-fill align-self-start">
                    <h4 class="name">{{ orderItem.item.name }}</h4>

                    <div class="options" v-if="orderItem.options.length > 0">
                      <display-value
                        class="my-0 options__item"
                        v-for="option of orderItem.options"
                        :key="option.index"
                        :title="
                          (option.optionItem.option.type === 'add' ||
                          option.val > 1
                            ? `${option.val}x `
                            : '') + option.optionItem.title
                        "
                        :client-bold="false"
                        :space-between="true"
                      >
                        <span class="font-weight-regular"
                          >{{
                            option.total > 0
                              ? getCurrency(order.currency).symbol
                              : ''
                          }}{{ option.total > 0 ? option.total : '-' }}</span
                        >
                      </display-value>
                    </div>

                    <div>
                      <span class="float-left"
                        >{{ getCurrency(order.currency).symbol
                        }}{{ orderItem.price }}</span
                      >
                      <span class="float-right font-weight-regular"
                        >{{ getCurrency(order.currency).symbol
                        }}{{ orderItem.total }}</span
                      >
                    </div>
                    <div class="clear"></div>
                    <display-value
                      class="comments"
                      :title="$t('order.comments')"
                      v-if="orderItem.comment"
                    >
                      <p>{{ orderItem.comment }}</p>
                    </display-value>
                  </v-col>
                </v-row>
              </div>
              <display-value
                class="comments font-size-9e"
                :title="$t('order.comments')"
                v-if="order.comment && order.comment.trim() !== ''"
              >
                <p>{{ order.comment }}</p>
              </display-value>
              <div class="text-right">
                <display-value
                  class="d-inline-block"
                  style="width: 200px"
                  :title="'Total pedido #' + (index + 1)"
                  :client-bold="false"
                  space-between
                >
                  <span class="font-weight-bold">{{
                    getCurrency(order.currency).symbol + order.total
                  }}</span>
                </display-value>
              </div>
            </div>
            <div class="group-total mt-4">
              <display-value
                :title="'Total de todos los pedidos'"
                :client-bold="false"
                space-between
              >
                <span class="font-weight-bold">{{
                  getCurrency(orders[0].currency).symbol + total
                }}</span>
              </display-value>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="cancel">{{ $t('close') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { IOrder } from '../../interfaces/IOrder';
  import DisplayValue from '../DisplayValue.vue';
  import { Globals } from '../../modules/Globals';
  import {
    OrderPaymentStatus,
    OrderPaymentStatusColor,
    OrderStatus,
    OrderStatusColor,
  } from '../../enums/OrderEnums';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import OrdersService from '../../api/services/orders.service';
  import { IOrderItem } from '../../interfaces/IOrderItem';
  import { IOrdersItem } from '../../interfaces/IOrdersItem';
  import { displayPrice } from '../../utils';

  @Component({
    components: { DisplayValue },
  })
  export default class OrderGroupModal extends Vue {
    @Prop() title: string;
    @Prop() orders: IOrder[];
    @Prop({ default: true }) reloadItems?: boolean;
    ordersService = new OrdersService();
    apiPath = Globals.apiPath;
    uploadsPath = Globals.uploadsPath;
    loading = false;
    initialized = false;

    async created() {
      if (this.reloadItems) {
        const promises = [];
        for (const order of this.orders) {
          promises.push(
            new Promise<void>(async (resolve) => {
              order.orderItems = await this.ordersService.getOrderItems(
                order.id
              );
              resolve();
            })
          );
        }
        await Promise.all(promises);
      }
      this.initialized = true;
    }

    get total() {
      return displayPrice(
        this.orders.reduce((total, order) => (total += order.total), 0)
      );
    }

    getCurrency(id: string) {
      return Globals.getCurrency(id);
    }

    getStatusColor(status: OrderStatus) {
      return OrderStatusColor[status] ?? 'grey';
    }

    getStatusName(status: OrderStatus) {
      return this.$tc(`order.status.${status}`);
    }

    getPaymentStatusColor(status: OrderPaymentStatus) {
      return OrderPaymentStatusColor[status] ?? 'grey';
    }

    getPaymentStatusName(status: OrderPaymentStatus) {
      switch (status) {
        case 'await':
          return this.$tc('order.paymentStatus.notPaid');
        case 'paid':
          return this.$tc('order.paymentStatus.paid');
        case 'declined':
          return this.$tc('order.paymentStatus.declined');
        case 'reimbursed':
          return this.$tc('order.paymentStatus.reimbursed');
      }
    }

    cancel() {
      this.$emit('close');
    }
  }
</script>
